import { useEffect, useMemo, useState } from 'react'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2, RotateCcw, Send, X } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  testBenchService,
  type TestBenchInjectInput,
  type TestBenchInjectResult,
  type TestBenchResetResult,
} from '@/services/testBenchService'

const PORTALS: NonNullable<TestBenchInjectInput['portal']>[] = ['idealista', 'fotocasa', 'pisos.com', 'habitaclia']

/**
 * Banco de pruebas: lo que RK necesita para probar el flujo completo una y
 * otra vez con los mismos móviles sin que la prueba anterior bloquee la
 * siguiente, y sin tener que publicar nada en Idealista/Fotocasa.
 */
export function TestBenchCard() {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  // --- Resets por número ---
  const [phones, setPhones] = useState<string[]>([])
  const [initialPhones, setInitialPhones] = useState<string[]>([])
  const [newPhone, setNewPhone] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetting, setResetting] = useState<string | null>(null)
  const [resetResults, setResetResults] = useState<Record<string, TestBenchResetResult>>({})

  // --- Inyector de leads ---
  const [inject, setInject] = useState<TestBenchInjectInput>({
    phone: '',
    id_inmueble: 0,
    agente_gestor: 0,
    nombre: '',
    portal: 'idealista',
    notas: '',
  })
  const [injecting, setInjecting] = useState(false)
  const [injectError, setInjectError] = useState<string | null>(null)
  const [injectResult, setInjectResult] = useState<TestBenchInjectResult | null>(null)

  useEffect(() => {
    let cancelled = false
    testBenchService
      .getPhones()
      .then((res) => {
        if (cancelled) return
        setPhones(res.phones)
        setInitialPhones(res.phones)
        setInject((prev) => ({ ...prev, phone: prev.phone || res.phones[0] || '' }))
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const hasChanges = useMemo(
    () => JSON.stringify(phones) !== JSON.stringify(initialPhones),
    [phones, initialPhones]
  )

  const addPhone = () => {
    const digits = newPhone.replace(/\D/g, '')
    if (digits.length < 9) return
    const e164 = digits.length === 9 ? `+34${digits}` : `+${digits}`
    if (!phones.includes(e164)) setPhones((prev) => [...prev, e164])
    setNewPhone('')
  }

  const savePhones = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await testBenchService.savePhones(phones)
      setPhones(res.phones)
      setInitialPhones(res.phones)
      toast.success(t('common.save'))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const resetOne = async (phone: string) => {
    setResetting(phone)
    setError(null)
    try {
      const res = await testBenchService.resetPhone(phone)
      setResetResults((prev) => ({ ...prev, [phone]: res }))
      toast.success(t('actions.test_bench.reset_done', { phone }))
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Reset failed'
      setError(message)
      toast.error(message)
    } finally {
      setResetting(null)
    }
  }

  const resetAll = async () => {
    for (const phone of initialPhones) {
      // Secuencial a propósito: cada reset toca el portal y no queremos 4 a la vez.
      await resetOne(phone)
    }
  }

  const submitInject = async () => {
    setInjecting(true)
    setInjectError(null)
    setInjectResult(null)
    try {
      const res = await testBenchService.injectLead({
        ...inject,
        id_inmueble: Number(inject.id_inmueble),
        agente_gestor: Number(inject.agente_gestor),
      })
      setInjectResult(res)
      toast.success(t('actions.test_bench.inject_done'))
    } catch (e) {
      setInjectError(e instanceof Error ? e.message : 'Inject failed')
    } finally {
      setInjecting(false)
    }
  }

  const canInject =
    Boolean(inject.phone) && Number(inject.id_inmueble) > 0 && Number(inject.agente_gestor) > 0 && !injecting

  const describeReset = (r: TestBenchResetResult) => {
    const portal = r.portal.ok
      ? t('actions.test_bench.reset_portal_ok', { n: r.portal.demandConsents ?? 0 })
      : t('actions.test_bench.reset_portal_failed', { error: r.portal.error ?? '' })
    return t('actions.test_bench.reset_summary', {
      executions: r.executions,
      conversations: r.conversations,
      messages: r.messages,
      meetings: r.meetings,
      portal,
    })
  }

  return (
    <SeccionPlegable
      etiqueta={t('actions.test_bench.chip_label')}
      titulo={t('actions.test_bench.title')}
      descripcion={t('actions.test_bench.description')}
      abierta={isOpen}
      onAlternar={() => setIsOpen((prev) => !prev)}
    >
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* --- Resets por número --- */}
            <section className="space-y-2 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h4 className="font-medium text-foreground">{t('actions.test_bench.reset_title')}</h4>
                  <p className="text-xs text-muted-foreground">{t('actions.test_bench.reset_help')}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={loading || Boolean(resetting) || initialPhones.length === 0}
                  onClick={resetAll}
                >
                  <RotateCcw className="mr-1 h-4 w-4" />
                  {t('actions.test_bench.reset_all')}
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </div>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {phones.map((phone) => {
                    const res = resetResults[phone]
                    return (
                      <li key={phone} className="flex flex-col gap-1 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-foreground">{phone}</span>
                          <div className="ml-auto flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={Boolean(resetting) || !initialPhones.includes(phone)}
                              onClick={() => resetOne(phone)}
                            >
                              {resetting === phone ? (
                                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                              ) : (
                                <RotateCcw className="mr-1 h-4 w-4" />
                              )}
                              {t('actions.test_bench.reset_one')}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t('common.delete')}
                              onClick={() => setPhones((prev) => prev.filter((p) => p !== phone))}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {res && <p className="text-xs text-muted-foreground">{describeReset(res)}</p>}
                      </li>
                    )
                  })}
                  {phones.length === 0 && (
                    <li className="px-3 py-3 text-sm text-muted-foreground">{t('actions.test_bench.no_phones')}</li>
                  )}
                </ul>
              )}

              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[200px] flex-1 space-y-1">
                  <Label htmlFor="test-bench-new-phone">{t('actions.test_bench.add_phone')}</Label>
                  <Input
                    id="test-bench-new-phone"
                    placeholder="+34 6XX XXX XXX"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addPhone()
                      }
                    }}
                  />
                </div>
                <Button variant="outline" size="sm" onClick={addPhone} disabled={newPhone.replace(/\D/g, '').length < 9}>
                  {t('common.add')}
                </Button>
                {hasChanges && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setPhones(initialPhones)} disabled={saving}>
                      {t('common.cancel')}
                    </Button>
                    <Button size="sm" onClick={savePhones} disabled={saving}>
                      {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                      {t('common.save')}
                    </Button>
                  </>
                )}
              </div>
            </section>

            {/* --- Inyector de leads --- */}
            <section className="space-y-2 p-4">
              <div>
                <h4 className="font-medium text-foreground">{t('actions.test_bench.inject_title')}</h4>
                <p className="text-xs text-muted-foreground">{t('actions.test_bench.inject_help')}</p>
              </div>

              <div className="grid gap-2 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>{t('actions.test_bench.inject_phone')}</Label>
                  <Select value={inject.phone} onValueChange={(v) => setInject((p) => ({ ...p, phone: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('actions.test_bench.inject_phone')} />
                    </SelectTrigger>
                    <SelectContent>
                      {initialPhones.map((phone) => (
                        <SelectItem key={phone} value={phone}>
                          {phone}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="test-bench-nombre">{t('actions.test_bench.inject_name')}</Label>
                  <Input
                    id="test-bench-nombre"
                    value={inject.nombre ?? ''}
                    onChange={(e) => setInject((p) => ({ ...p, nombre: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="test-bench-inmueble">{t('actions.test_bench.inject_property')}</Label>
                  <Input
                    id="test-bench-inmueble"
                    inputMode="numeric"
                    placeholder="627498"
                    value={inject.id_inmueble || ''}
                    onChange={(e) => setInject((p) => ({ ...p, id_inmueble: Number(e.target.value.replace(/\D/g, '')) }))}
                  />
                  <p className="text-[11px] text-muted-foreground">{t('actions.test_bench.inject_property_help')}</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="test-bench-gestor">{t('actions.test_bench.inject_agent')}</Label>
                  <Input
                    id="test-bench-gestor"
                    inputMode="numeric"
                    placeholder="418805"
                    value={inject.agente_gestor || ''}
                    onChange={(e) =>
                      setInject((p) => ({ ...p, agente_gestor: Number(e.target.value.replace(/\D/g, '')) }))
                    }
                  />
                  <p className="text-[11px] text-muted-foreground">{t('actions.test_bench.inject_agent_help')}</p>
                </div>
                <div className="space-y-1">
                  <Label>{t('actions.test_bench.inject_portal')}</Label>
                  <Select
                    value={inject.portal}
                    onValueChange={(v) => setInject((p) => ({ ...p, portal: v as TestBenchInjectInput['portal'] }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PORTALS.map((portal) => (
                        <SelectItem key={portal} value={portal}>
                          {portal}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="test-bench-notas">{t('actions.test_bench.inject_notes')}</Label>
                  <Input
                    id="test-bench-notas"
                    value={inject.notas ?? ''}
                    onChange={(e) => setInject((p) => ({ ...p, notas: e.target.value }))}
                  />
                </div>
              </div>

              {injectError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{injectError}</AlertDescription>
                </Alert>
              )}

              {injectResult && (
                <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-foreground">
                  <div>
                    <strong>IdLead</strong> {injectResult.row.IdLead} · <strong>{t('actions.test_bench.result_status')}</strong>{' '}
                    {injectResult.result.status}
                  </div>
                  <div>
                    <strong>WhatsApp</strong>{' '}
                    {injectResult.result.whatsapp_template?.skipped
                      ? t('actions.test_bench.result_wa_skipped')
                      : injectResult.result.whatsapp_template?.status ?? '—'}
                    {injectResult.result.whatsapp_template?.error
                      ? ` (${injectResult.result.whatsapp_template.error})`
                      : ''}
                  </div>
                  <div>
                    <strong>{t('actions.test_bench.result_demand')}</strong>{' '}
                    {injectResult.result.rk_demand?.ok
                      ? t('actions.test_bench.result_demand_ok')
                      : injectResult.result.rk_demand?.reason || injectResult.result.rk_demand?.error || '—'}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button size="sm" onClick={submitInject} disabled={!canInject}>
                  {injecting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                  {t('actions.test_bench.inject_submit')}
                </Button>
              </div>
            </section>
    </SeccionPlegable>
  )
}
